import { useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useUser } from '@/queries/useUser';
import { useStoreData } from '@/queries/useStoreData';
import { hasActiveSubscription } from '@/features/subscriptions/logic/subscriptionStatus.logic';
import { isPaidProduct } from '@/lib/helpers';
import { GENERIC_STATUS, INTEGRATION_TYPE, STRIPE_ACCOUNT_STATUS } from '@/lib/constants';
import { IntegrationData } from '@/types/user';

/**
 * Hook to determine if the Stripe setup banner should be shown
 * Shows when user has active subscription, paid products, but no direct deposit setup
 * Excludes dashboard and income pages (they have their own banners)
 */
export function useStripeSetupBanner() {
    const { user } = useUser();
    const location = useLocation();
    const { data: storeData } = useStoreData(user?.default_store_id);

    const hasStripeAccountIssues = useMemo(() => {
        const integrations = user?.integrations || {};
        const stripeIntegration = integrations[INTEGRATION_TYPE.STRIPE_CONNECT] as IntegrationData | undefined;
        return (
            stripeIntegration?.data?.account_status &&
            [
                STRIPE_ACCOUNT_STATUS.REJECTED,
                STRIPE_ACCOUNT_STATUS.RESTRICTED,
                STRIPE_ACCOUNT_STATUS.RESTRICTED_SOON,
            ].includes(stripeIntegration.data.account_status)
        );
    }, [user]);

    const hasActiveSubscriptionValue = useMemo(() => {
        return hasActiveSubscription(user);
    }, [user]);

    const hasPaidProductsNoDirectDeposit = useMemo(() => {
        if (!storeData || storeData.length === 0) return false;
        const store = storeData[0];
        if (!store?.pages) return false;

        // Check if user has any paid products
        const hasPaidProducts = store.pages.some(page => {
            if (!page.data?.product) return false;
            return isPaidProduct(page.data.product);
        });

        if (!hasPaidProducts) return false;

        return !!user?.payment_restricted?.linksite;
    }, [storeData, user]);

    const hasOnboardingDashboard = useMemo(() => {
        return user?.['onboarding-dashboard']?.status === GENERIC_STATUS.ACTIVE;
    }, [user]);

    const isOnDashboardPage = useMemo(() => {
        return location.pathname === '/dashboard';
    }, [location.pathname]);

    const isOnIncomePage = useMemo(() => {
        return location.pathname === '/income';
    }, [location.pathname]);

    const showStripeSetupBanner = useMemo(() => {
        // Dashboard and income pages have their own banner
        if (hasStripeAccountIssues && (isOnDashboardPage || isOnIncomePage)) {
            return false;
        }

        if (!hasActiveSubscriptionValue || !hasPaidProductsNoDirectDeposit) {
            return false;
        }

        // If user has onboarding, only show banner if past 2nd step + not on dashboard page
        // (dashboard page already shows setup direct deposit)
        if (hasOnboardingDashboard) {
            const profileImageUploaded = user?.['onboarding-dashboard']?.profile_image_uploaded;
            const socialLinksAdded = user?.['onboarding-dashboard']?.social_links_added;

            if (!profileImageUploaded || !socialLinksAdded || isOnDashboardPage) {
                return false;
            }
        }

        return true;
    }, [
        hasStripeAccountIssues,
        isOnDashboardPage,
        isOnIncomePage,
        hasActiveSubscriptionValue,
        hasPaidProductsNoDirectDeposit,
        hasOnboardingDashboard,
        user,
    ]);

    return {
        showStripeSetupBanner,
    };
}
